import type {
  BuildingDef,
  HumanCommandSource,
  MatchState,
  PermanentBuildingKind,
  PlayerId,
} from '@signalclash/shared';
import { BUILDINGS, MAX_HUBS_PER_PLAYER, PERMANENT_BUILDING_KINDS } from '@signalclash/shared';

import type { BuildMode } from '../input/BuildMode.js';

const STYLE_ID = 'build-panel-styles';

// Unicode glyphs that appear above the button label for quick visual
// identification. Defined as constants so a single edit covers both the
// button DOM and any future tooltips or accessible descriptions.
const ICON_HUB = '⊞';
const ICON_TURRET = '⛉';

interface BuildPanelOptions {
  playerId: PlayerId;
  human: HumanCommandSource;
  buildMode: BuildMode;
}

interface ButtonRef {
  kind: PermanentBuildingKind;
  el: HTMLButtonElement;
  defLabel: HTMLElement;
}

// V0.4 build panel (DOM overlay) — V4 retains it as a placement indicator only.
// The bulk of the build affordance now lives inside the HUD's Command Card
// (when the player's own Core / Hub is selected) so the screen is no longer
// permanently occupied by Hub/Turret buttons. While build mode is armed this
// strip surfaces a "click a slot near your Core. ESC to cancel." prompt that
// stays in screen-center where the player's attention naturally lands during
// placement, and gives a fallback button row in case the Core wasn't already
// selected. Two buttons (Production Hub, Turret) showing cost + build time +
// a disabled state when constraints fail. Clicking arms build-mode for that
// kind; the next click on a Core slot in the world becomes a BuildCommand
// via InputRouter → HumanCommandSource. ESC and clicking the armed button
// again cancel.
//
// The panel reads sim snapshots to grey out buttons when:
//   - the player can't afford the cost,
//   - the player already owns ≥2 Hubs (Production Hub only),
//   - all 6 slots on the player's Core are occupied.
export class BuildPanel {
  // Mutable so the network-mode `setPlayerId` can flip the panel from
  // its constructor-time PLAYER_ZERO default to the server-assigned
  // slot before the first snapshot arrives.
  private opts: BuildPanelOptions;
  private container: HTMLDivElement | null = null;
  private hint: HTMLDivElement | null = null;
  private readonly buttons: ButtonRef[] = [];
  private latest: MatchState | null = null;
  private armedKind: PermanentBuildingKind | null = null;
  // V4 — driven by show()/hide() (scene transitions) + arming state, so the
  // panel disappears completely when the player isn't placing a building.
  private sceneVisible = false;

  constructor(opts: BuildPanelOptions) {
    this.opts = opts;
    injectStyles();
    this.mount();
    opts.buildMode.subscribe((kind) => this.onArmedChanged(kind));
  }

  setPlayerId(playerId: PlayerId): void {
    this.opts = { ...this.opts, playerId };
    if (this.latest !== null) this.update(this.latest);
  }

  hide(): void {
    this.sceneVisible = false;
    this.applyVisibility();
  }

  show(): void {
    this.sceneVisible = true;
    this.applyVisibility();
  }

  update(state: MatchState): void {
    this.latest = state;
    this.refreshButtons();
  }

  private mount(): void {
    this.container = document.createElement('div');
    this.container.id = 'build-panel';
    this.container.className = 'build-panel';

    const title = document.createElement('div');
    title.className = 'build-panel__title';
    title.textContent = 'BUILD (B)';
    this.container.appendChild(title);

    const row = document.createElement('div');
    row.className = 'build-panel__row';
    for (const kind of PERMANENT_BUILDING_KINDS) {
      const def = BUILDINGS[kind];
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'build-panel__btn';
      btn.dataset.kind = kind;

      const name = document.createElement('div');
      name.className = 'build-panel__name';
      name.textContent = labelOf(kind);

      const icon = document.createElement('div');
      icon.className = 'build-panel__icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = kind === 'production_hub' ? ICON_HUB : ICON_TURRET;

      btn.appendChild(icon);
      btn.appendChild(name);

      const meta = document.createElement('div');
      meta.className = 'build-panel__meta';
      meta.textContent = metaOf(def);
      btn.appendChild(meta);

      btn.addEventListener('click', () => this.onClicked(kind));
      row.appendChild(btn);
      this.buttons.push({ kind, el: btn, defLabel: meta });
    }
    this.container.appendChild(row);

    this.hint = document.createElement('div');
    this.hint.className = 'build-panel__hint';
    this.hint.textContent = '';
    this.container.appendChild(this.hint);

    document.body.appendChild(this.container);
  }

  private onClicked(kind: PermanentBuildingKind): void {
    this.opts.buildMode.arm(kind);
  }

  private onArmedChanged(kind: PermanentBuildingKind | null): void {
    this.armedKind = kind;
    for (const ref of this.buttons) {
      ref.el.dataset.armed = ref.kind === kind ? 'true' : 'false';
    }
    if (this.hint !== null) {
      this.hint.textContent = kind === null ? '' : 'Click a slot near your Core. ESC to cancel.';
    }
    // V4 — track armed state in the panel's visibility so the strip vanishes
    // when the player exits build-mode (selection-driven command card carries
    // the build buttons the rest of the time).
    if (this.container !== null) {
      this.container.classList.toggle('build-panel--placement', kind !== null);
    }
    this.applyVisibility();
  }

  // V4 — the bottom-center strip is hidden when the player isn't actively
  // placing a building. The HUD's Command Card already shows the Build row
  // when the player has their own Core / Hub selected, so the always-on
  // panel just added visual clutter.
  private applyVisibility(): void {
    if (this.container === null) return;
    const shouldShow = this.sceneVisible && this.armedKind !== null;
    this.container.style.display = shouldShow ? 'flex' : 'none';
  }

  private refreshButtons(): void {
    const state = this.latest;
    if (state === null) return;
    const player = state.players[this.opts.playerId];
    let hubsOwned = 0;
    let slotsOpen = 0;
    let coreId: number | null = null;
    for (const c of Object.values(state.cores)) {
      if (c.ownerId === this.opts.playerId) coreId = c.id;
    }
    if (coreId !== null) {
      const occupied = new Set<number>();
      for (const b of Object.values(state.buildings)) {
        if (b.kind === 'mini_turret') continue;
        if (b.coreId === coreId) occupied.add(b.slotIndex);
        if (b.ownerId === this.opts.playerId && b.kind === 'production_hub') hubsOwned += 1;
      }
      for (let i = 0; i < 6; i++) if (!occupied.has(i)) slotsOpen += 1;
    }
    for (const ref of this.buttons) {
      const def = BUILDINGS[ref.kind];
      const cantAfford = player.signal < def.cost;
      const noSlot = slotsOpen === 0;
      const hubLimit = ref.kind === 'production_hub' && hubsOwned >= MAX_HUBS_PER_PLAYER;
      const disabled = cantAfford || noSlot || hubLimit;
      ref.el.disabled = disabled;
      ref.el.title = cantAfford
        ? `Need ${def.cost} Signal`
        : hubLimit
          ? `Already at ${MAX_HUBS_PER_PLAYER} Hubs`
          : noSlot
            ? 'No build slots open'
            : '';
      // Don't keep the panel visually 'armed' if the kind can no longer be
      // afforded — the user might keep clicking and wonder why nothing happens.
      if (disabled && this.armedKind === ref.kind) {
        this.opts.buildMode.cancel();
      }
    }
  }
}

function labelOf(kind: PermanentBuildingKind): string {
  if (kind === 'production_hub') return 'Production Hub';
  return 'Turret';
}

function metaOf(def: BuildingDef): string {
  return `${def.cost}S · ${def.buildTimeSec}s`;
}

function injectStyles(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
.build-panel {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 12;
  display: none;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  background: rgba(0,0,0,0.7);
  border: 1px solid #2a3038;
  border-radius: 6px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  color: #cfd6dd;
  pointer-events: auto;
}
.build-panel__title {
  font-size: 11px;
  letter-spacing: 0.12em;
  color: #8c95a0;
  text-transform: uppercase;
}
.build-panel__row { display: flex; gap: 8px; }
.build-panel__btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  font-family: inherit;
  background: #1f262e;
  color: #eef3f8;
  border: 1px solid #3a444f;
  border-radius: 4px;
  padding: 8px 14px;
  min-width: 130px;
  cursor: pointer;
  letter-spacing: 0.02em;
}
.build-panel__btn:hover:not(:disabled) {
  background: #2a3340;
  border-color: #5a6878;
}
.build-panel__btn:disabled { opacity: 0.45; cursor: not-allowed; }
.build-panel__btn[data-armed="true"] {
  background: #2c3a4a;
  border-color: #f5e76e;
  color: #f5e76e;
}
.build-panel__name { font-size: 12px; }
.build-panel__meta { font-size: 11px; color: #8c95a0; }
.build-panel__hint {
  font-size: 11px;
  color: #f5e76e;
  min-height: 14px;
}
/* ── Per-building-type icon and colour accent ───────────────────────────── */
.build-panel__icon {
  font-size: 20px;
  line-height: 1;
  margin-bottom: 2px;
}
.build-panel__btn[data-kind="production_hub"] .build-panel__icon { color: #f5e76e; }
.build-panel__btn[data-kind="production_hub"]:not(:disabled):not([data-armed="true"]) {
  border-color: #4a4020;
}
.build-panel__btn[data-kind="production_hub"]:not(:disabled):not([data-armed="true"]):hover {
  background: #2a2a18;
  border-color: #c8b840;
  color: #f5e76e;
}
/* Turret: red-orange accent — defensive / offensive building theme */
.build-panel__btn:not([data-kind="production_hub"]) .build-panel__icon { color: #ff9670; }
.build-panel__btn:not([data-kind="production_hub"]):not(:disabled):not([data-armed="true"]) {
  border-color: #4a2418;
}
.build-panel__btn:not([data-kind="production_hub"]):not(:disabled):not([data-armed="true"]):hover {
  background: #281a14;
  border-color: #e2543c;
  color: #ff9670;
}
/* Active/press feedback */
.build-panel__btn:active:not(:disabled) { transform: scale(0.96); }

/* V5 mobile UX pass — the standalone BuildPanel is hidden on mobile. Its two
 * jobs (Hub/Turret picker, "Tap a slot" hint) are already covered by the
 * in-card Build row inside the HUD context panel and by the ArmedBanner.
 * Hiding it eliminates the bottom-stack collision (3-4 panels competing for
 * the strip above the action bar) and gives the context panel room to sit
 * right above the action bar where the thumb already is. */
@media (max-width: 820px), (pointer: coarse) {
  .build-panel { display: none !important; }
}
`;
  document.head.appendChild(style);
}
